import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Paper, Box, Typography, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Grid, Avatar, Divider, FormControl, InputLabel, Select,
  MenuItem, TextField, useTheme, alpha, Tooltip, Collapse, Autocomplete, Badge
} from '@mui/material';

import { deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

// Icons
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimelineIcon from '@mui/icons-material/Timeline';
import LabelIcon from '@mui/icons-material/Label';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SaveIcon from '@mui/icons-material/Save';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarChartIcon from '@mui/icons-material/BarChart';
import ImageIcon from '@mui/icons-material/Image';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TuneIcon from '@mui/icons-material/Tune';
import ApartmentIcon from '@mui/icons-material/Apartment';

// Constants
const dayOptions = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const sessionOptions = ['ניו יורק', 'לונדון', 'טוקיו', 'סידני'];
const reportOptions = [
  'אין דיווח',
  'חג',
  'אירוע חדשותי',
  'אירוע פד',
  'אירוע FOMC',
  'החלטת ריבית',
  'מדד המחירים לצרכן (CPI)',
  'ליבת מדד המחירים לצרכן',
  'מדד המחירים ליצרן (PPI)',
  'דוח תעסוקה (Non-Farm Payroll)',
  'שיעור אבטלה',
  'תוצר מקומי גולמי (GDP)',
  'מכירות קמעונאיות',
  'אמון הצרכנים',
  'דוח OPEC',
  'מלאי נפט גולמי',
  'אירוע G7',
  'סקר PMI',
  'פרוטוקול ישיבת הפד',
  'מדד מחירי הדיור',
  'הוצאות צרכנים',
  'דוח תקציב ממשלתי',
  'מכירות בתים חדשים',
  'תמ"ג',
  'הזמנות מוצרים בני-קיימא',
  'דוח תנודתיות 3',
];

const outcomeOptions = ['Take Profit', 'Stop Loss'];
const triggerOptions = ['BUTTC/RBTI', 'Flight/Wing Break', 'UT/SP', 'AMD', 'אחר'];
const timeframeOptions = ['1m', '3m', '5m', '15m', '1h', '4h', '12h', '1d', '3d', '1w'];
const phaseOptions = ['A', 'B', 'C', 'D', 'E', '', 'לא ברור'];
const symbolOptions = ['BTC', 'ETH', 'XAUUSD', 'EURUSD', 'GBPUSD', 'NQ', 'BTC'];
// Table columns
const columns = [
  { id: 'date', label: 'תאריך' },
  { id: 'dayOfWeek', label: 'יום' },
  { id: 'time', label: 'שעה' },
  { id: 'triggerAppeared', label: 'טריגר?' },
  { id: 'matchedTrigger', label: 'סוג טריגר' },
  { id: 'session', label: 'סשן' },
  { id: 'symbol', label: 'סימול' },
  { id: 'report', label: 'דיווח' },
  { id: 'stopPercentage', label: 'סטופ %' },
  { id: 'outcome', label: 'תוצאה' },
  { id: 'tpSelected', label: 'יעדי TP' },
  { id: 'duration', label: 'משך זמן (דק׳)' },
  { id: 'linkBefore', label: 'תמונה לפני' },
  { id: 'linkAfter', label: 'תמונה אחרי' },
  { id: 'userName', label: 'משתמש' },
];

// AMD time windows
const amdTimeWindows = [
  { id: 'window1', label: 'חלון 1', timeRange: '9:00 - 11:30', description: 'חלון בוקר' },
  { id: 'window2', label: 'חלון 2', timeRange: '15:10 - 15:45', description: 'חלון צהריים' },
  { id: 'window3', label: 'חלון 3', timeRange: '16:50 - 17:15', description: 'חלון ערב' }
];

// TP options with half R
const tpOptions = [];
for (let value = 0.0; value <= 250; value += 0.5) {
  tpOptions.push(`1:${value}`);
}

// Function to get TP options based on outcome
const getTPOptionsForOutcome = (outcome) => {
  if (outcome === 'Stop Loss') {
    // For Stop Loss, include 0 as an option (already included in tpOptions)
    return tpOptions;
  }
  return tpOptions;
};

// Helper functions
function parseTimeStringToDate(timeStr) {
  if (!timeStr) return null;
  try {
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  } catch (error) {
    console.error('שגיאה בעיבוד זמן:', error);
    return null;
  }
}

function safeParseDate(dateStr) {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch (error) {
    console.error('שגיאה בעיבוד תאריך:', error);
    return null;
  }
}

function formatDateToTimeString(date) {
  if (!date) return '';
  try {
    // Check if date is a valid Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  } catch (error) {
    console.error('שגיאה בעיבוד זמן:', error);
    return '';
  }
}

function getSessionFromUserProfileTrigger(trig) {
  if (trig === 'BUTTC/RBTI' || trig === 'Flight/Wing Break') return 'ניו יורק';
  if (trig === 'UT/SP') return 'לונדון';
  return '';
}

function isSessionLockedByProfileTrigger(trig) {
  return trig === 'BUTTC/RBTI' || trig === 'Flight/Wing Break' || trig === 'UT/SP';
}

const TradeTable = ({ trades, user, userProfile, showErrorMessage }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Image dialog states
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogUrl, setImageDialogUrl] = useState('');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Detail dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailTrade, setDetailTrade] = useState(null);
  
  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTrade, setEditTrade] = useState(null);
  const [editStopReasonDialogOpen, setEditStopReasonDialogOpen] = useState(false);
  const [editStopReasonValue, setEditStopReasonValue] = useState('');
  const [editReportDialogOpen, setEditReportDialogOpen] = useState(false);
  const [editNewReport, setEditNewReport] = useState({ report: 'אין דיווח', time: '' });
  
  // Delete confirmation states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tradeToDelete, setTradeToDelete] = useState(null);

  // Filter expanded state
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  // Handle filtering
  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      // If value is empty, remove the filter
      if (value === '' || value === null || value === undefined) {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      }
      
      return { ...prev, [field]: value };
    });
  };
  
  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  // Apply filters and sorting
  const filteredAndSortedTrades = React.useMemo(() => {
    // First apply search query across multiple fields
    let filtered = trades;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(trade => 
        (trade.symbol && trade.symbol.toLowerCase().includes(query)) ||
        (trade.matchedTrigger && trade.matchedTrigger.toLowerCase().includes(query)) ||
        (trade.session && trade.session.toLowerCase().includes(query)) ||
        (trade.userName && trade.userName.toLowerCase().includes(query)) ||
        (trade.report && trade.report.toLowerCase().includes(query))
      );
    }
    
    // Then apply specific filters
    filtered = filtered.filter(trade => {
      for (let key in filters) {
        // Skip empty filters
        if (filters[key] === '' || filters[key] === undefined) continue;
        
        // Handle array filters like reports
        if (key === 'reports' && filters[key] && trade.reports) {
          const reportExists = trade.reports.some(r => 
            r.report && r.report.includes(filters[key])
          );
          if (!reportExists) return false;
          continue;
        }
        
        // Handle date range filter
        if (key === 'dateFrom' && filters[key]) {
          if (trade.date < filters[key]) return false;
          continue;
        }
        
        if (key === 'dateTo' && filters[key]) {
          if (trade.date > filters[key]) return false;
          continue;
        }
        
        // Handle outcome filter
        if (key === 'outcome' && filters[key]) {
          if (trade.outcome !== filters[key]) return false;
          continue;
        }
        
        // Handle trigger filter
        if (key === 'triggerAppeared' && filters[key] !== undefined) {
          if (trade.triggerAppeared !== filters[key]) return false;
          continue;
        }
        
        // Standard string matching
        if (trade[key] === undefined || !String(trade[key]).includes(String(filters[key]))) {
          return false;
        }
      }
      return true;
    });
    
    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        // Ensure the values exist
        const aValue = a[sortConfig.key] !== undefined ? a[sortConfig.key] : '';
        const bValue = b[sortConfig.key] !== undefined ? b[sortConfig.key] : '';
        
        // Special handling for dates
        if (sortConfig.key === 'date') {
          const aDate = safeParseDate(aValue);
          const bDate = safeParseDate(bValue);
          
          // Handle null dates (invalid dates)
          if (aDate === null && bDate === null) return 0;
          if (aDate === null) return sortConfig.direction === 'asc' ? 1 : -1; // Nulls at end for asc
          if (bDate === null) return sortConfig.direction === 'asc' ? -1 : 1; // Nulls at end for asc
          
          return sortConfig.direction === 'asc' 
            ? aDate - bDate
            : bDate - aDate;
        }
        
        // Numeric comparison
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // String comparison
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }
    
    return filtered;
  }, [trades, sortConfig, filters, searchQuery]);

  // Image dialog functions
  const openImageDialog = (url) => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setImageDialogUrl(url);
    setImageDialogOpen(true);
  };
  
  // Detail dialog functions
  const handleDetailClick = (trade) => {
    setDetailTrade(trade);
    setDetailDialogOpen(true);
  };
  
  // Edit dialog functions
  const handleEditClick = (trade) => {
    try {
      // Ensure phases structure exists
      if (!trade.phases) {
        trade.phases = {
          '3m': '',
          '5m': '',
          '15m': '',
          '1h': '',
          '4h': '',
          '1d': '',
          '1w': '',
        };
      }
      
      // Ensure achievedRValues structure exists
      if (!trade.achievedRValues) {
        trade.achievedRValues = {};
      }
      
      // Ensure fiboRValues structure exists
      if (!trade.fiboRValues) {
        trade.fiboRValues = {};
      }
    
    const newEditTrade = { ...trade };
  
    try {
      if (trade.tpMilestones && Array.isArray(trade.tpMilestones)) {
        newEditTrade.tpMilestonesReached = trade.tpMilestones
          .filter((m) => m && m.status && (m.status === 'הושג' || m.status === 'Reached'))
          .map((m) => m.tp)
          .filter(Boolean);
        newEditTrade.tpMilestoneExit =
          trade.tpMilestones.find((m) => m && m.status && (m.status === 'יציאה' || m.status === 'Exit'))?.tp || '';
      } else {
        newEditTrade.tpMilestonesReached = [];
        newEditTrade.tpMilestoneExit = '';
      }
    } catch (error) {
      console.error('שגיאה בעיבוד tpMilestones:', error);
      newEditTrade.tpMilestonesReached = [];
      newEditTrade.tpMilestoneExit = '';
    }
    
    // Convert single report to reports array if needed
    if (trade.report && (!trade.reports || trade.reports.length === 0)) {
      newEditTrade.reports = [
        { report: trade.report, time: trade.time || '' }
      ];
    }
  
    setEditStopReasonValue(newEditTrade.stopReason || '');
    setEditTrade(newEditTrade);
    setEditDialogOpen(true);
    } catch (error) {
      console.error('שגיאה בפתיחת עריכת עסקה:', error);
      showErrorMessage('שגיאה בפתיחת עריכת עסקה');
    }
  };
  
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === 'outcome') {
      if (value === 'Stop Loss') {
        setEditStopReasonDialogOpen(true);
        setEditTrade((prev) => ({ ...prev, outcome: value, tpMilestoneExit: "-1R", finalTP: [] }));
      } else {
        setEditStopReasonValue('');
        setEditTrade((prev) => ({ ...prev, outcome: value }));
      }
      return;
    }
    
    if (name === 'triggerAppeared') {
      if (value === 'no') {
        setEditTrade((prev) => ({
          ...prev,
          triggerAppeared: 'no',
          matchedTrigger: '',
          outcome: '',
          stopPercentage: '',
          duration: '',
          tpMilestonesReached: [],
          tpMilestoneExit: '',
          stopReason: '',
          amdTimeWindow: '',
          finalTP: [],
          phases: {
            '3m': '',
            '5m': '',
            '15m': '',
            '1h': '',
            '4h': '',
            '1d': '',
            '1w': '',
          },
          mainTimeframe: '15m',
          tradeDirection: 'long',
          tpRecommendation: '',
          phasesWithC: [],
          achievedRValues: {},
          fiboRValues: {}
        }));
      } else {
        setEditTrade((prev) => ({ ...prev, triggerAppeared: 'yes' }));
      }
      return;
    }
    
    if (name === 'matchedTrigger') {
      // Special handling for AMD trigger
      if (value === 'AMD') {
        setEditTrade((prev) => ({ 
          ...prev, 
          matchedTrigger: value,
          symbol: 'GBPUSD', // Auto-set GBPUSD for AMD
          amdTimeWindow: '',
        }));
      } else {
        setEditTrade((prev) => ({ 
          ...prev, 
          matchedTrigger: value,
          amdTimeWindow: '',
        }));
      }
      return;
    }
    
    // Handle date and time fields
    if (name === 'date') {
      if (!value) {
        setEditTrade((prev) => ({ ...prev, date: '', dayOfWeek: '' }));
      } else {
        try {
          const jsDate = new Date(value);
          if (isNaN(jsDate.getTime())) {
            setEditTrade((prev) => ({ ...prev, date: '', dayOfWeek: '' }));
          } else {
            const dayIndex = jsDate.getDay();
            const dayName = dayOptions[dayIndex];
            setEditTrade((prev) => ({
              ...prev,
              date: value,
              dayOfWeek: dayName,
            }));
          }
        } catch (error) {
          console.error('שגיאה בעיבוד תאריך:', error);
          setEditTrade((prev) => ({ ...prev, date: '', dayOfWeek: '' }));
        }
      }
    } else if (name === 'time') {
      setEditTrade((prev) => ({ ...prev, time: value }));
    } else {
      setEditTrade((prev) => ({ ...prev, [name]: value }));
    }
  };
  
  // Handle phase change in edit mode
  const handleEditPhaseChange = (timeframe, phase) => {
    setEditTrade((prev) => ({
      ...prev,
      phases: {
        ...prev.phases,
        [timeframe]: phase
      }
    }));
  };
  

  
  // Handle TP milestones in edit mode
  const handleEditMilestonesReachedChange = (event, newValue) => {
    setEditTrade((prev) => ({ ...prev, tpMilestonesReached: newValue }));
  };

  const handleEditMilestoneExitChange = (e) => {
    setEditTrade((prev) => ({ ...prev, tpMilestoneExit: e.target.value }));
  };

  const handleEditFinalTPChange = (event, newValue) => {
    setEditTrade((prev) => ({ ...prev, finalTP: newValue }));
  };
  
  // Report dialog in edit mode
  const handleOpenEditReportDialog = () => {
    setEditNewReport({ report: 'אין דיווח', time: '' });
    setEditReportDialogOpen(true);
  };
  
  const handleCloseEditReportDialog = () => {
    setEditReportDialogOpen(false);
  };
  
  const handleAddEditReport = () => {
    if (!editNewReport.report || !editNewReport.time) {
      showErrorMessage("נא למלא את כל שדות הדיווח");
      return;
    }
    
    setEditTrade((prev) => ({
      ...prev,
      reports: [...(prev.reports || []), editNewReport]
    }));
    
    setEditReportDialogOpen(false);
  };
  
  const handleRemoveEditReport = (index) => {
    setEditTrade((prev) => ({
      ...prev,
      reports: (prev.reports || []).filter((_, i) => i !== index)
    }));
  };
  
  const handleEditReportChange = (e) => {
    setEditNewReport({
      ...editNewReport,
      [e.target.name]: e.target.value
    });
  };
  

  
  // Stop reason dialog in edit mode
  const handleCloseEditStopReasonDialog = () => {
    setEditStopReasonDialogOpen(false);
  };
  
  const handleSaveEditStopReason = () => {
    setEditStopReasonDialogOpen(false);
  };
  
  // Save edit changes
  const handleSaveEdit = async () => {
    if (!editTrade) return;
    try {
      const tradeId = editTrade.id;
      const docRef = doc(db, 'trades', tradeId);
  
      const milestones = editTrade.tpMilestonesReached.map((tp) => ({
        tp,
        status: 'הושג',
      }));
      
      if (editTrade.tpMilestoneExit && editTrade.outcome !== 'Stop Loss') {
        milestones.push({ tp: editTrade.tpMilestoneExit, status: 'יציאה' });
      } else if(editTrade.outcome === 'Stop Loss'){
        milestones.push({ tp: "-1R", status: 'יציאה' });
      }
      
      // Combine reports for backward compatibility
      const legacyReport = editTrade.reports && editTrade.reports.length > 0 
        ? editTrade.reports[0].report 
        : 'אין דיווח';
  
      const updatedTradeData = {
        ...editTrade,
        tpMilestones: milestones,
        stopReason: editStopReasonValue,
        report: legacyReport,
      };
      delete updatedTradeData.tpMilestonesReached;
      delete updatedTradeData.tpMilestoneExit;
  
      await updateDoc(docRef, updatedTradeData);
      setEditDialogOpen(false);
      setEditTrade(null);
    } catch (err) {
      console.error('שגיאה בעדכון עסקה:', err);
      showErrorMessage(`שגיאה בעדכון עסקה: ${err.message}`);
    }
  };
  
  // Delete trade functions
  const handleDeleteClick = (trade) => {
    setTradeToDelete(trade);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tradeToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'trades', tradeToDelete.id));
      setDeleteConfirmOpen(false);
      setTradeToDelete(null);
    } catch (err) {
      console.error('שגיאה במחיקת עסקה:', err);
      showErrorMessage(`שגיאה במחיקה: ${err.message}`);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setTradeToDelete(null);
  };

  // Styling
  const styles = {
    container: {
      borderRadius: 0,
      overflow: 'hidden',
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      direction: 'rtl',
      textAlign: 'right'
    },
    tableHeader: {
      py: 2, 
      px: 3, 
      bgcolor: alpha(theme.palette.primary.main, 0.05), 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`
    },
    tableHeadCell: {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
      py: 2,
      cursor: 'pointer',
      userSelect: 'none',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
      },
      transition: 'background-color 0.2s ease',
    },
    actionButton: {
      borderRadius: 8,
      boxShadow: 'none',
      height: 40,
      minWidth: 40,
      '&:hover': {
        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
      },
    },
    actionIconButton: {
      bgcolor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.15)
        : alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.25),
      },
      borderRadius: 8,
      padding: 1,
      width: 36,
      height: 36,
    },
    tableCell: {
      fontSize: '0.85rem',
      padding: '12px 16px',
      maxWidth: 200,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    sortIcon: {
      fontSize: '1rem',
      marginRight: 0.5,
      transition: 'transform 0.2s ease',
    },
    statusChip: {
      borderRadius: 12,
      fontWeight: 500,
      height: 24,
      '& .MuiChip-label': {
        padding: '0 10px',
      },
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      maxWidth: 400,
    },
    searchField: {
      '& .MuiInputBase-root': {
        borderRadius: 8,
        height: 40,
        backgroundColor: alpha(theme.palette.background.default, 0.6),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.divider, 0.2),
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.primary.main, 0.3),
      },
      flexGrow: 1,
    },
    filterContainer: {
      marginBottom: 2,
      padding: 2,
      borderRadius: 1,
      backgroundColor: alpha(theme.palette.background.default, 0.6),
      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
    },
    filterTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: 1,
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: 1,
      },
    },
    detailBox: {
      marginBottom: 3,
      padding: 2.5,
      borderRadius: 2,
      backgroundColor: alpha(theme.palette.background.paper, 0.4),
      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    },
    detailBoxHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 2,
      paddingBottom: 1,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    },
    dialogTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingBottom: 1,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    },
    closeButton: {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.2),
      },
    },
    imagePreview: {
      width: 60,
      height: 60,
      objectFit: 'cover',
      borderRadius: 1.5,
      cursor: 'pointer',
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
      transition: 'transform 0.2s, border-color 0.2s',
      '&:hover': {
        transform: 'scale(1.05)',
        borderColor: theme.palette.primary.main,
      },
    },
    emptyState: {
      textAlign: 'center',
      padding: 4,
      color: theme.palette.text.secondary,
    },
    inputField: {
      '& .MuiInputBase-root': {
        borderRadius: 10,
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        transition: 'all 0.2s',
        height: 48,
        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        '&:hover': {
          backgroundColor: alpha(theme.palette.background.default, 0.5),
          boxShadow: '0 4px 8px rgba(0,0,0,0.06)',
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
          borderColor: theme.palette.primary.main,
        }
      },
    },
  };

  return (
    <>
      <Paper sx={styles.container}>
        {/* Table Header */}
        <Box sx={styles.tableHeader}>
          <Typography variant="h6" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <ShowChartIcon sx={{ ml: 1.5 }} />
            טבלת עסקאות
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Search Field */}
            <Box sx={styles.searchContainer}>
              <TextField
                placeholder="חיפוש עסקאות..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ ml: 1 }} />,
                }}
                sx={styles.searchField}
              />
              <Tooltip title="פתח מסננים">
                <IconButton 
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)} 
                  sx={{
                    ...styles.actionIconButton,
                    bgcolor: isFilterExpanded 
                      ? alpha(theme.palette.primary.main, 0.2) 
                      : alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <Badge 
                    badgeContent={Object.keys(filters).length} 
                    color="primary" 
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                  >
                    <FilterListIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              <Tooltip title="רענן">
                <IconButton 
                  sx={styles.actionIconButton}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
        
        {/* Filters Section */}
        <Collapse in={isFilterExpanded}>
          <Box sx={styles.filterContainer}>
            <Typography 
              variant="subtitle2" 
              sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
            >
              <FilterListIcon sx={{ ml: 1, fontSize: '1.2rem', color: theme.palette.primary.main }} />
              אפשרויות סינון
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={styles.inputField}>
                  <InputLabel>סימול</InputLabel>
                  <Select
                    value={filters.symbol || ''}
                    onChange={(e) => handleFilterChange('symbol', e.target.value)}
                    label="סימול"
                  >
                    <MenuItem value="">הכל</MenuItem>
                    {symbolOptions.map(sym => (
                      <MenuItem key={sym} value={sym}>{sym}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={styles.inputField}>
                  <InputLabel>טריגר</InputLabel>
                  <Select
                    value={filters.triggerAppeared || ''}
                    onChange={(e) => handleFilterChange('triggerAppeared', e.target.value)}
                    label="טריגר"
                  >
                    <MenuItem value="">הכל</MenuItem>
                    <MenuItem value="yes">כן</MenuItem>
                    <MenuItem value="no">לא</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={styles.inputField}>
                  <InputLabel>סשן</InputLabel>
                  <Select
                    value={filters.session || ''}
                    onChange={(e) => handleFilterChange('session', e.target.value)}
                    label="סשן"
                  >
                    <MenuItem value="">הכל</MenuItem>
                    {sessionOptions.map(session => (
                      <MenuItem key={session} value={session}>{session}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={styles.inputField}>
                  <InputLabel>תוצאה</InputLabel>
                  <Select
                    value={filters.outcome || ''}
                    onChange={(e) => handleFilterChange('outcome', e.target.value)}
                    label="תוצאה"
                  >
                    <MenuItem value="">הכל</MenuItem>
                    {outcomeOptions.map(outcome => (
                      <MenuItem key={outcome} value={outcome}>{outcome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="outlined" 
                color="error" 
                size="small" 
                onClick={handleClearFilters}
                sx={{ ml: 1 }}
              >
                נקה מסננים
              </Button>
              <Button 
                variant="outlined" 
                color="primary" 
                size="small" 
                onClick={() => setIsFilterExpanded(false)}
              >
                סגור מסננים
              </Button>
            </Box>
          </Box>
        </Collapse>

        {/* Table */}
        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={styles.tableHeadCell}>פעולות</TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align="center"
                    onClick={() => handleSort(column.id)}
                    sx={{
                      ...styles.tableHeadCell,
                      position: 'relative',
                      ...(sortConfig.key === column.id && {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {sortConfig.key === column.id && (
                        <SortIcon 
                          sx={{
                            ...styles.sortIcon,
                            transform: sortConfig.direction === 'asc' ? 'rotate(0deg)' : 'rotate(180deg)',
                          }}
                        />
                      )}
                      {column.label}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedTrades.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={styles.emptyState}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>לא נמצאו עסקאות</Typography>
                    <Typography variant="body2">
                      {Object.keys(filters).length > 0 ? 
                        'נסה לנקות חלק מהמסננים' : 
                        'הוסף את העסקה הראשונה שלך כדי להתחיל'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedTrades
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((trade) => (
                    <TableRow 
                      key={trade.id}
                      hover
                      sx={{
                        '&:nth-of-type(even)': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        },
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="מחק">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(trade)}
                              sx={{ 
                                ...styles.actionIconButton,
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                color: theme.palette.error.main,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.2),
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="ערוך">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(trade)}
                              sx={{ 
                                ...styles.actionIconButton,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="הצג פרטים">
                            <IconButton
                              size="small"
                              onClick={() => handleDetailClick(trade)}
                              sx={{ 
                                ...styles.actionIconButton,
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.info.main, 0.2),
                                }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {columns.map((column) => {
                        if (column.id === 'linkBefore' || column.id === 'linkAfter') {
                          const url = trade[column.id];
                          return (
                            <TableCell key={column.id} align="center" sx={styles.tableCell}>
                              {url ? (
                                <Box
                                  component="img"
                                  src={url}
                                  alt={column.id}
                                  sx={styles.imagePreview}
                                  onClick={() => openImageDialog(url)}
                                />
                              ) : null}
                            </TableCell>
                          );
                        } else if (column.id === 'triggerAppeared') {
                          return (
                            <TableCell key={column.id} align="center" sx={styles.tableCell}>
                              {trade[column.id] === 'yes' ? (
                                <Chip 
                                  size="small" 
                                  icon={<CheckCircleIcon fontSize="small" />} 
                                  label="כן" 
                                  color="success"
                                  variant="outlined"
                                  sx={styles.statusChip}
                                />
                              ) : (
                                <Chip 
                                  size="small" 
                                  icon={<CancelIcon fontSize="small" />}
                                  label="לא" 
                                  color="default"
                                  variant="outlined"
                                  sx={styles.statusChip}
                                />
                              )}
                            </TableCell>
                          );
                        } else if (column.id === 'outcome') {
                          return (
                            <TableCell key={column.id} align="center" sx={styles.tableCell}>
                              {trade[column.id] === 'Take Profit' ? (
                                <Chip 
                                  size="small" 
                                  label="Take Profit" 
                                  color="success"
                                  variant="outlined"
                                  sx={styles.statusChip}
                                />
                              ) : trade[column.id] === 'Stop Loss' ? (
                                <Chip 
                                  size="small" 
                                  label="Stop Loss" 
                                  color="error"
                                  variant="outlined"
                                  sx={styles.statusChip}
                                />
                              ) : (
                                trade[column.id]
                              )}
                            </TableCell>
                          );
                        } else if (column.id === 'tpSelected') {
                          return (
                            <TableCell key={column.id} align="center" sx={styles.tableCell}>
                              {trade.tpMilestones ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                  {trade.tpMilestones.length > 3 ? (
                                    <Chip
                                      size="small"
                                      label={`${trade.tpMilestones.length} נקודות`}
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => handleDetailClick(trade)}
                                      sx={{
                                        ...styles.statusChip,
                                        fontSize: '0.75rem',
                                        cursor: 'pointer'
                                      }}
                                    />
                                  ) : (
                                    trade.tpMilestones.map((m, idx) => (
                                      <Chip
                                        key={idx}
                                        size="small"
                                        label={m.tp}
                                        variant="outlined"
                                        color={(m.status === 'הושג' || m.status === 'Reached') ? 'primary' : 'default'}
                                        sx={{
                                          ...styles.statusChip,
                                          fontSize: '0.7rem',
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              ) : ''}
                            </TableCell>
                          );
                        } else if (column.id === 'report') {
                          return (
                            <TableCell key={column.id} align="center" sx={styles.tableCell}>
                              {/* Display multiple reports if available */}
                              {trade.reports && trade.reports.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                  {trade.reports.length <= 2 ? (
                                    // Show up to 2 reports directly
                                    trade.reports.map((report, idx) => (
                                      <Chip
                                        key={idx}
                                        size="small"
                                        label={`${report.report.substring(0, 15)}${report.report.length > 15 ? '...' : ''} ${report.time ? `(${report.time})` : ''}`}
                                        variant="outlined"
                                        color="info"
                                        sx={{
                                          ...styles.statusChip,
                                          fontSize: '0.7rem',
                                        }}
                                      />
                                    ))
                                  ) : (
                                    // Show count if more than 2
                                    <Chip
                                      size="small"
                                      label={`${trade.reports.length} דיווחים`}
                                      variant="outlined"
                                      color="info"
                                      sx={{
                                        ...styles.statusChip,
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => handleDetailClick(trade)}
                                    />
                                  )}
                                </Box>
                              ) : (
                                // Show single report if no reports array
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                  {trade.report || 'אין דיווח'}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        } else {
                          return (
                            <TableCell 
                              key={column.id} 
                              align="center"
                              sx={styles.tableCell}
                            >
                              {trade[column.id]}
                            </TableCell>
                          );
                        }
                      })}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredAndSortedTrades.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="שורות לעמוד"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} מתוך ${count !== -1 ? count : `יותר מ-${to}`}`
          }
          sx={{ 
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        />
      </Paper>

      {/* Image Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl'
          },
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <IconButton 
            onClick={() => setImageDialogOpen(false)}
            size="small"
            sx={styles.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight="bold">מציג תמונה</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {imageDialogUrl && (
            <Box
              component="img"
              src={imageDialogUrl}
              alt="תמונת גרף"
              sx={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                transition: 'transform 0.2s ease',
                cursor: isDragging ? 'grabbing' : 'grab',
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              }}
              // Mouse wheel event for zooming
              onWheel={(e) => {
                e.preventDefault();
                const rect = e.currentTarget.getBoundingClientRect();
                const pointerX = e.clientX - rect.left;
                const pointerY = e.clientY - rect.top;
                const oldZoom = zoom;
                const delta = e.deltaY > 0 ? 0.9 : 1.1;
                const newZoom = Math.min(Math.max(oldZoom * delta, 1), 3);
                const newOffsetX = offset.x - (pointerX - offset.x) * (newZoom / oldZoom - 1);
                const newOffsetY = offset.y - (pointerY - offset.y) * (newZoom / oldZoom - 1);
                setZoom(newZoom);
                setOffset({ x: newOffsetX, y: newOffsetY });
              }}
              // Events for dragging
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDragging(true);
                setDragStart({ x: e.clientX, y: e.clientY });
              }}
              onMouseMove={(e) => {
                if (!isDragging) return;
                const dx = e.clientX - dragStart.x;
                const dy = e.clientY - dragStart.y;
                setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
                setDragStart({ x: e.clientX, y: e.clientY });
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
          
          {/* Zoom controls */}
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: 16, 
              left: 16, 
              display: 'flex', 
              gap: 1,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              padding: '4px',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
            }}
          >
            <IconButton 
              size="small" 
              onClick={() => setZoom(Math.min(zoom + 0.2, 3))}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => setZoom(Math.max(zoom - 0.2, 0.5))}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                }
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }}
              sx={{ 
                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.secondary.main, 0.2),
                }
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Trade Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <IconButton 
            onClick={() => setDetailDialogOpen(false)}
            size="small"
            sx={styles.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="secondary">פרטי עסקה</Typography>
            <Avatar sx={{ 
              bgcolor: alpha(theme.palette.secondary.main, 0.2), 
              color: theme.palette.secondary.main,
              width: 36,
              height: 36,
              ml: 1.5
            }}>
              <InfoIcon />
            </Avatar>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailTrade && (
            <Box sx={{ mt: 2, p: 1 }}>
              {/* Basic Info */}
              <Box sx={styles.detailBox}>
                <Box sx={styles.detailBoxHeader}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">תאריך ושעה:</Typography>
                    <CalendarTodayIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">תאריך:</Typography>
                    <Typography>{detailTrade.date}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">יום:</Typography>
                    <Typography>{detailTrade.dayOfWeek}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">שעה:</Typography>
                    <Typography>{detailTrade.time}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Symbol & User */}
              <Box sx={styles.detailBox}>
                <Box sx={styles.detailBoxHeader}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">סימול ומשתמש:</Typography>
                    <ShowChartIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">סימול:</Typography>
                    <Typography fontWeight="500">{detailTrade.symbol}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">משתמש:</Typography>
                    <Typography>{detailTrade.userName || user.displayName || user.email}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Reports */}
              <Box sx={styles.detailBox}>
                <Box sx={styles.detailBoxHeader}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" color="info.main">דיווחים:</Typography>
                    <EventNoteIcon color="info" sx={{ mr: 1, fontSize: 20 }} />
                  </Box>
                </Box>
                
                {detailTrade.reports && detailTrade.reports.length > 0 ? (
                  <Box sx={{ 
                    maxHeight: 200, 
                    overflowY: 'auto', 
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 2,
                    p: 0
                  }}>
                    {detailTrade.reports.map((reportItem, index) => (
                      <Box key={index} sx={{ 
                        p: 2, 
                        borderBottom: index < detailTrade.reports.length - 1 ? 
                          `1px solid ${alpha(theme.palette.divider, 0.2)}` : 'none',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <Typography>{reportItem.report}</Typography>
                        {reportItem.time && (
                          <Typography color="text.secondary" fontSize="0.9rem">
                            בשעה {reportItem.time}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography>
                    {detailTrade.report || 'אין דיווח'}
                  </Typography>
                )}
              </Box>

              {detailTrade.triggerAppeared === 'yes' && (
                <>
                  {/* Trigger Details */}
                  <Box sx={styles.detailBox}>
                    <Box sx={styles.detailBoxHeader}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="secondary">פרטי טריגר:</Typography>
                        <LabelIcon color="secondary" sx={{ mr: 1, fontSize: 20 }} />
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">טריגר תואם:</Typography>
                        <Typography fontWeight="500">{detailTrade.matchedTrigger || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">סשן:</Typography>
                        <Typography fontWeight="500">{detailTrade.session || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">סטופ %:</Typography>
                        <Typography fontWeight="500">{detailTrade.stopPercentage || '-'}%</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">משך זמן:</Typography>
                        <Typography fontWeight="500">{detailTrade.duration || '-'} דקות</Typography>
                      </Grid>
                      <Grid item xs={12} md={12}>
                        <Typography variant="body2" color="text.secondary">תוצאה:</Typography>
                        <Box sx={{ mt: 1 }}>
                          {detailTrade.outcome === 'Take Profit' ? (
                            <Chip 
                              label="Take Profit" 
                              color="success" 
                              variant="outlined"
                              sx={{ fontWeight: 'bold', borderRadius: 8 }}
                            />
                          ) : detailTrade.outcome === 'Stop Loss' ? (
                            <Chip 
                              label="Stop Loss" 
                              color="error" 
                              variant="outlined"
                              sx={{ fontWeight: 'bold', borderRadius: 8 }}
                            />
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </Box>
                      </Grid>
                      {detailTrade.outcome === 'Stop Loss' && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">סיבת סטופ לוס:</Typography>
                          <Typography fontWeight="500">{detailTrade.stopReason || '-'}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Phases & Timeframes */}
                  {detailTrade.phases && (
                    <Box sx={styles.detailBox}>
                      <Box sx={styles.detailBoxHeader}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold" color="primary">פאזות וטיימפריימס:</Typography>
                          <BarChartIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                        </Box>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary">טיימפריים ראשי:</Typography>
                          <Typography fontWeight="500">{detailTrade.mainTimeframe || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary">כיוון עסקה:</Typography>
                          <Typography fontWeight="500" sx={{ display: 'flex', alignItems: 'center' }}>
                            {detailTrade.tradeDirection === 'long' ? (
                              <>
                                לונג
                                <TrendingUpIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                              </>
                            ) : 
                             detailTrade.tradeDirection === 'short' ? (
                              <>
                                שורט
                                <TrendingDownIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                              </>
                            ) : '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" color="text.secondary">המלצת TP:</Typography>
                          <Typography fontWeight="500" color="success.main">
                            {detailTrade.tpRecommendation || '-'}
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>פאזות לפי טיימפריים:</Typography>
                        <TableContainer component={Paper} sx={{ 
                          mt: 1, 
                          boxShadow: 'none', 
                          border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                          borderRadius: 2,
                          overflow: 'hidden'
                        }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <TableRow>
                                {timeframeOptions.map(tf => (
                                  <TableCell key={tf} align="center" sx={{ fontWeight: 'bold' }}>{tf}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                {timeframeOptions.map(tf => (
                                  <TableCell key={`phase-${tf}`} align="center" sx={{
                                    ...(tf === detailTrade.mainTimeframe ? {
                                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                                      fontWeight: 'bold'
                                    } : {})
                                  }}>
                                    {detailTrade.phases[tf] || '-'}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                      
                      {/* R Values - normal */}
                      {detailTrade.achievedRValues && Object.keys(detailTrade.achievedRValues).length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            ערכי R שהושגו ב-ICE/CREEK לפי טיימפריים:
                          </Typography>
                          <TableContainer component={Paper} sx={{ 
                            mt: 1, 
                            boxShadow: 'none', 
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}>
                            <Table size="small">
                              <TableHead sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                                <TableRow>
                                  {Object.keys(detailTrade.achievedRValues).map(tf => (
                                    <TableCell key={tf} align="center" sx={{ fontWeight: 'bold' }}>
                                      טיימפריים {tf}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  {Object.keys(detailTrade.achievedRValues).map(tf => (
                                    <TableCell key={`rvalue-${tf}`} align="center">
                                      <Typography variant="body2" fontWeight="bold" color="success.main">
                                        {detailTrade.achievedRValues[tf]}R
                                      </Typography>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                      
                      {/* R Values - Fibonacci */}
                      {detailTrade.fiboRValues && Object.keys(detailTrade.fiboRValues).length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            ערכי R שהושגו ביעדי פיבונאצ'י לפי טיימפריים:
                          </Typography>
                          <TableContainer component={Paper} sx={{ 
                            mt: 1, 
                            boxShadow: 'none', 
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            borderRadius: 2,
                            overflow: 'hidden'
                          }}>
                            <Table size="small">
                              <TableHead sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                                <TableRow>
                                  {Object.keys(detailTrade.fiboRValues).map(tf => (
                                    <TableCell key={tf} align="center" sx={{ fontWeight: 'bold' }}>
                                      טיימפריים {tf} - פיבונאצ'י
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow>
                                  {Object.keys(detailTrade.fiboRValues).map(tf => (
                                    <TableCell key={`fibovalue-${tf}`} align="center">
                                      <Typography variant="body2" fontWeight="bold" color="info.main">
                                        {detailTrade.fiboRValues[tf]}R
                                      </Typography>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* TP Milestones */}
                  <Box sx={styles.detailBox}>
                    <Box sx={styles.detailBoxHeader}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">נתוני TP:</Typography>
                        <WhatshotIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                      </Box>
                    </Box>
                    {detailTrade.tpMilestones && detailTrade.tpMilestones.length > 0 ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>יעדי TP שהושגו:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
                          {detailTrade.tpMilestones.map((m, idx) => (
                            <Chip
                              key={idx}
                              label={`${m.tp} ${(m.status === 'יציאה' || m.status === 'Exit') ? '(יציאה)' : ''}`}
                              variant="outlined"
                              color={(m.status === 'הושג' || m.status === 'Reached') ? 'primary' : (m.status === 'יציאה' || m.status === 'Exit') ? 'success' : 'default'}
                              size="small"
                              sx={{ borderRadius: 8 }}
                            />
                          ))}
                        </Box>
                      </>
                    ) : (
                      <Typography>אין נתוני TP</Typography>
                    )}
                    {detailTrade.finalTP && detailTrade.finalTP.length > 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          TP סופי בסגירת החלון הנוכחי:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                          {detailTrade.finalTP.map((tp, idx) => (
                            <Chip
                              key={idx}
                              label={tp}
                              variant="outlined"
                              color="info"
                              size="small"
                              sx={{ borderRadius: 8 }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                </>
              )}

              {/* Images */}
              <Box sx={styles.detailBox}>
                <Box sx={styles.detailBoxHeader}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">תמונות:</Typography>
                    <ImageIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                  </Box>
                </Box>
                <Grid container spacing={3}>
                  {detailTrade.linkBefore && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>תמונה לפני:</Typography>
                      <Box 
                        component="img"
                        src={detailTrade.linkBefore}
                        alt="תמונה לפני"
                        sx={{
                          width: '100%',
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        onClick={() => openImageDialog(detailTrade.linkBefore)}
                      />
                    </Grid>
                  )}
                  {detailTrade.linkAfter && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>תמונה אחרי:</Typography>
                      <Box 
                        component="img"
                        src={detailTrade.linkAfter}
                        alt="תמונה אחרי"
                        sx={{
                          width: '100%',
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          cursor: 'pointer',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                        onClick={() => openImageDialog(detailTrade.linkAfter)}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button 
            variant="contained" 
            onClick={() => setDetailDialogOpen(false)}
            sx={{
              borderRadius: 8,
              height: 44,
              width: 120,
              margin: '0 auto'
            }}
          >
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}

        <Dialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)} 
          fullWidth 
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              direction: 'rtl',
              textAlign: 'right'
            },
          }}
        >
          <DialogTitle sx={styles.dialogTitle}>
            <IconButton 
              onClick={() => setEditDialogOpen(false)}
              size="small"
              sx={styles.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold" color="primary">עריכת עסקה</Typography>
              <Avatar sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.2), 
                color: theme.palette.primary.main,
                width: 36,
                height: 36,
                ml: 1.5
              }}>
                <EditIcon />
              </Avatar>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            {editTrade && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Basic Details */}
                <Box sx={styles.detailBox}>
                  <Box sx={styles.detailBoxHeader}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        פרטים בסיסיים
                      </Typography>
                      <TuneIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    </Box>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                        <InputLabel>סימול</InputLabel>
                        <Select
                          name="symbol"
                          value={editTrade.symbol || 'BTC'}
                          onChange={handleEditChange}
                          label="סימול"
                        >
                          {symbolOptions.map((sym) => (
                            <MenuItem key={sym} value={sym}>
                              {sym}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="תאריך"
                        type="date"
                        name="date"
                        value={editTrade.date || ''}
                        onChange={handleEditChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={styles.inputField}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="שעה"
                        type="time"
                        name="time"
                        value={editTrade.time || ''}
                        onChange={handleEditChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={styles.inputField}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                        <InputLabel>יום בשבוע</InputLabel>
                        <Select
                          name="dayOfWeek"
                          value={editTrade.dayOfWeek || ''}
                          onChange={handleEditChange}
                          label="יום בשבוע"
                        >
                          {dayOptions.map((day) => (
                            <MenuItem key={day} value={day}>
                              {day}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                        <InputLabel>האם היה טריגר?</InputLabel>
                        <Select
                          name="triggerAppeared"
                          value={editTrade.triggerAppeared || 'no'}
                          onChange={handleEditChange}
                          label="האם היה טריגר?"
                        >
                          <MenuItem value="no">לא</MenuItem>
                          <MenuItem value="yes">כן</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>

                {/* Trigger Details */}
                {editTrade.triggerAppeared === 'yes' && (
                  <Box sx={styles.detailBox}>
                    <Box sx={styles.detailBoxHeader}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          פרטי טריגר
                        </Typography>
                        <ApartmentIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                      </Box>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                          <InputLabel>טריגר תואם</InputLabel>
                          <Select
                            name="matchedTrigger"
                            value={editTrade.matchedTrigger || ''}
                            onChange={handleEditChange}
                            label="טריגר תואם"
                          >
                            {triggerOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                          <InputLabel>סשן</InputLabel>
                          <Select
                            name="session"
                            value={editTrade.session || ''}
                            onChange={handleEditChange}
                            label="סשן"
                          >
                            {sessionOptions.map((s) => (
                              <MenuItem key={s} value={s}>
                                {s}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="אחוז סטופ"
                          name="stopPercentage"
                          type="number"
                          value={editTrade.stopPercentage || ''}
                          onChange={handleEditChange}
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={styles.inputField}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                          <InputLabel>כיוון עסקה</InputLabel>
                          <Select
                            name="tradeDirection"
                            value={editTrade.tradeDirection || 'long'}
                            onChange={handleEditChange}
                            label="כיוון עסקה"
                          >
                            <MenuItem value="long">לונג</MenuItem>
                            <MenuItem value="short">שורט</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                          <InputLabel>טיימפריים ראשי</InputLabel>
                          <Select
                            name="mainTimeframe"
                            value={editTrade.mainTimeframe || '15m'}
                            onChange={handleEditChange}
                            label="טיימפריים ראשי"
                          >
                            {timeframeOptions.map((tf) => (
                              <MenuItem key={tf} value={tf}>
                                {tf}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="משך זמן (דקות)"
                          name="duration"
                          type="number"
                          value={editTrade.duration || ''}
                          onChange={handleEditChange}
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={styles.inputField}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                          <InputLabel>תוצאה</InputLabel>
                          <Select
                            name="outcome"
                            value={editTrade.outcome || ''}
                            onChange={handleEditChange}
                            label="תוצאה"
                          >
                            {outcomeOptions.map((o) => (
                              <MenuItem key={o} value={o}>
                                {o}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} md={8}>
                        <Autocomplete
                          multiple
                          freeSolo
                          options={getTPOptionsForOutcome(editTrade.outcome)}
                          value={editTrade.tpMilestonesReached || []}
                          onChange={handleEditMilestonesReachedChange}
                          size="small"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="יעדי TP שהושגו"
                              placeholder="בחר יעדים או הקלד ערך מותאם"
                              fullWidth
                              sx={styles.inputField}
                            />
                          )}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              paddingY: '4.5px',
                            }
                          }}
                        />
                      </Grid>
                      
                      {editTrade.outcome !== 'Stop Loss' ? (
                        <>
                          <Grid item xs={12} md={4}>
                            <Autocomplete
                              freeSolo
                              options={tpOptions.concat(['סטופ'])}
                              value={editTrade.tpMilestoneExit || ''}
                              onChange={(event, newValue) => {
                                setEditTrade((prev) => ({ ...prev, tpMilestoneExit: newValue }));
                              }}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label="נקודת יציאה"
                                  placeholder="בחר או הקלד ערך מותאם"
                                  fullWidth
                                  sx={styles.inputField}
                                />
                              )}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  paddingY: '4.5px',
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <Autocomplete
                              multiple
                              freeSolo
                              options={tpOptions}
                              value={editTrade.finalTP || []}
                              onChange={handleEditFinalTPChange}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  label="TP סופי"
                                  placeholder="בחר TP סופי או הקלד ערך מותאם"
                                  fullWidth
                                  sx={styles.inputField}
                                />
                              )}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  paddingY: '4.5px',
                                }
                              }}
                            />
                          </Grid>
                        </>
                      ) : (
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                            <InputLabel>סיבת סטופ לוס</InputLabel>
                            <Select
                              value={editStopReasonValue}
                              onChange={(e) => setEditStopReasonValue(e.target.value)}
                              label="סיבת סטופ לוס"
                            >
                              <MenuItem value="חלק מהסטטיסטיקה (הביצוע היה מושלם)">
                                חלק מהסטטיסטיקה (הביצוע היה מושלם)
                              </MenuItem>
                              <MenuItem value="חוסר סינרגיה">חוסר סינרגיה</MenuItem>
                              <MenuItem value="שורט ב-ace בפאזה B">שורט ב-ace בפאזה B</MenuItem>
                              <MenuItem value="לונג ב-ace בפאזה B">לונג ב-ace בפאזה B</MenuItem>
                              <MenuItem value="הצבת סטופ חמדנית מדי">הצבת סטופ חמדנית מדי</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
                
                {/* Reports */}
                <Box sx={styles.detailBox}>
                  <Box sx={styles.detailBoxHeader}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        דיווחים
                      </Typography>
                      <EventNoteIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    </Box>
                  </Box>
                  
                  {editTrade.reports && editTrade.reports.length > 0 ? (
                    <Box sx={{ 
                      maxHeight: 200, 
                      overflowY: 'auto', 
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: 2,
                      mb: 2
                    }}>
                      {editTrade.reports.map((reportItem, index) => (
                        <Box key={index} sx={{ 
                          p: 2, 
                          borderBottom: index < editTrade.reports.length - 1 ? 
                            `1px solid ${alpha(theme.palette.divider, 0.2)}` : 'none',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="body2">
                            <strong>{reportItem.report}</strong>
                            {reportItem.time && ` בשעה ${reportItem.time}`}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemoveEditReport(index)}
                            sx={{ 
                              color: theme.palette.error.main,
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.2),
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                      לא הוגדרו דיווחים
                    </Typography>
                  )}
                  
                  <Button
                    startIcon={<AddIcon />}
                    fullWidth
                    variant="outlined"
                    size="small"
                    onClick={handleOpenEditReportDialog}
                    sx={{
                      borderRadius: 8,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      height: 42,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }}
                  >
                    הוסף דיווח
                  </Button>
                </Box>

                {/* Chart Images */}
                <Box sx={styles.detailBox}>
                  <Box sx={styles.detailBoxHeader}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        תמונות גרף
                      </Typography>
                      <ImageIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    </Box>
                  </Box>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="תמונה לפני (URL)"
                        name="linkBefore"
                        value={editTrade.linkBefore || ''}
                        onChange={handleEditChange}
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={styles.inputField}
                      />
                    </Grid>
                    
                    {editTrade.triggerAppeared === 'yes' && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="תמונה אחרי (URL)"
                          name="linkAfter"
                          value={editTrade.linkAfter || ''}
                          onChange={handleEditChange}
                          fullWidth
                          variant="outlined"
                          size="small"
                          sx={styles.inputField}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={() => setEditDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 8,
                height: 44
              }}
            >
              ביטול
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveEdit}
              sx={{
                borderRadius: 8,
                height: 44,
                px: 4
              }}
            >
              שמור שינויים
            </Button>
          </DialogActions>
        </Dialog>

        {/* Stop Reason Dialog (Edit) */}
        <Dialog
          open={editStopReasonDialogOpen}
          onClose={handleCloseEditStopReasonDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              direction: 'rtl',
              textAlign: 'right'
            },
          }}
        >
          <DialogTitle sx={styles.dialogTitle}>
            <IconButton 
              onClick={handleCloseEditStopReasonDialog}
              size="small"
              sx={styles.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" color="error" sx={{ display: 'flex', alignItems: 'center' }}>
              <ErrorOutlineIcon sx={{ ml: 1 }} />
              סיבת סטופ לוס
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 1, ...styles.inputField }}>
              <InputLabel>בחר סיבה</InputLabel>
              <Select
                label="בחר סיבה"
                value={editStopReasonValue}
                onChange={(e) => setEditStopReasonValue(e.target.value)}
              >
                <MenuItem value="חלק מהסטטיסטיקה (הביצוע היה מושלם)">
                  חלק מהסטטיסטיקה (הביצוע היה מושלם)
                </MenuItem>
                <MenuItem value="חוסר סינרגיה">חוסר סינרגיה</MenuItem>
                <MenuItem value="שורט ב-ace בפאזה B">שורט ב-ace בפאזה B</MenuItem>
                <MenuItem value="לונג ב-ace בפאזה B">לונג ב-ace בפאזה B</MenuItem>
                <MenuItem value="הצבת סטופ חמדנית מדי">הצבת סטופ חמדנית מדי</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={handleCloseEditStopReasonDialog}
              variant="outlined"
              sx={{
                borderRadius: 8,
                height: 44
              }}
            >
              ביטול
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSaveEditStopReason}
              sx={{
                borderRadius: 8,
                height: 44
              }}
            >
              שמור
            </Button>
          </DialogActions>
        </Dialog>

        {/* Report Dialog (Edit) */}
        <Dialog
          open={editReportDialogOpen}
          onClose={handleCloseEditReportDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              overflow: 'hidden',
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              direction: 'rtl',
              textAlign: 'right'
            },
          }}
        >
          <DialogTitle sx={styles.dialogTitle}>
            <IconButton 
              onClick={handleCloseEditReportDialog}
              size="small"
              sx={styles.closeButton}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <EventNoteIcon sx={{ ml: 1 }} />
              הוסף דיווח
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <FormControl fullWidth variant="outlined" size="small" sx={styles.inputField}>
                <InputLabel>סוג דיווח</InputLabel>
                <Select
                  name="report"
                  value={editNewReport.report}
                  onChange={handleEditReportChange}
                  label="סוג דיווח"
                >
                  {reportOptions.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                label="שעה"
                type="time"
                name="time"
                value={editNewReport.time || ''}
                onChange={handleEditReportChange}
                fullWidth
                variant="outlined"
                size="small"
                sx={styles.inputField}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              onClick={handleCloseEditReportDialog}
              variant="outlined"
              sx={{
                borderRadius: 8,
                height: 44
              }}
            >
              ביטול
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleAddEditReport}
              sx={{
                borderRadius: 8,
                height: 44
              }}
            >
              הוסף דיווח
            </Button>
          </DialogActions>
        </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            direction: 'rtl',
            textAlign: 'right'
          },
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          <IconButton 
            onClick={handleDeleteCancel}
            size="small"
            sx={styles.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" color="error" sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorOutlineIcon sx={{ ml: 1 }} />
            אישור מחיקה
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            האם אתה בטוח שברצונך למחוק את העסקה הזו?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            פעולה זו אינה ניתנת לביטול.
          </Typography>
          
          {tradeToDelete && (
            <Paper sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: alpha(theme.palette.error.main, 0.03),
              border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
              borderRadius: 2
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">תאריך:</Typography>
                  <Typography variant="body1">{tradeToDelete.date}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">סימול:</Typography>
                  <Typography variant="body1">{tradeToDelete.symbol}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">שעה:</Typography>
                  <Typography variant="body1">{tradeToDelete.time}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">תוצאה:</Typography>
                  <Typography variant="body1" color={
                    tradeToDelete.outcome === 'Take Profit' ? 'success.main' : 
                    tradeToDelete.outcome === 'Stop Loss' ? 'error.main' : 'text.primary'
                  }>
                    {tradeToDelete.outcome || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              borderRadius: 8,
              height: 44
            }}
          >
            ביטול
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteConfirm}
            sx={{
              borderRadius: 8,
              height: 44
            }}
          >
            מחק
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TradeTable;